import { useState, useCallback, useMemo, useEffect } from "react";
import { Box, Typography, Paper, Stack, IconButton, Button, CircularProgress, Skeleton } from "@mui/material";
import Iconify from "src/components/iconify";
import { useGetBrandSelectionQuery, useGetCountriesQuery, useTrackBrandMutation, useUntrackBrandMutation } from "src/context/api/brandPulse/api";
import { formatNumber } from "src/utils/format-number";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { CountryFilter, useCountryData } from "src/components/visibility";
import { Helmet } from "react-helmet-async";
import AddBrandModal from "./AddBrandModal";
import { useTranslation } from "react-i18next";
import CustomTooltip from "src/components/visibility/components/CustomTooltip";
import { useLocales } from "src/locales";
import RegionSelector from "./region-selector";
import { useSelector } from "react-redux";

// ----------------------------------------------------------------------

export type SuggestedBrand = {
  name: string;
  domains: string[];
  competitor_paid_search_volume: string;
  region: string;
};

export type TrackedBrand = {
  name: string;
  dbId: number;  // Database ID for deletion
  domains: string[];
  competitor_paid_search_volume: string;
  region: string;
};

// ----------------------------------------------------------------------

function SuggestedBrandCard({
  brand,
  isSelected = false,
  onToggle,
}: {
  brand: SuggestedBrand;
  isSelected?: boolean;
  onToggle?: (brand: SuggestedBrand) => void;
}) {
  const { t } = useTranslation();

  return (
    <Paper
      variant="outlined"
      onClick={() => onToggle?.(brand)}
      sx={{
        position: "relative",
        borderRadius: 1,
        overflow: "hidden",
        flexShrink: 0,
        cursor: "pointer",
        transition: "all 0.15s ease-in-out",
        // Selected state styles
        ...(isSelected && {
          borderColor: "#408cff",
          borderWidth: "1.6px",
          background: "linear-gradient(90deg, rgba(0, 119, 238, 0.06) 0%, rgba(0, 119, 238, 0.06) 100%), #fff",
        }),
        // Default state styles
        ...(!isSelected && {
          borderColor: "rgba(145, 158, 171, 0.2)",
          bgcolor: "background.paper",
          "&:hover": {
            borderColor: "rgba(64, 140, 255, 0.5)",
            boxShadow: "0 0 0 1px rgba(64, 140, 255, 0.1)",
          },
        }),
      }}
    >
      {/* Main content */}
      <Box sx={{ p: 1.5, pb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        {/* Header with logo and name */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            component="img"
            src={`https://www.google.com/s2/favicons?domain=${brand.domains[0]}&sz=48`}
            alt={brand.name}
            sx={{
              width: 24,
              height: 24,
              borderRadius: 0.5,
              objectFit: "cover",
            }}
          />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }}>
            {brand.name}
          </Typography>
        </Stack>

        {/* Domains list */}
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {brand.domains.join(", ")}
        </Typography>
      </Box>

      {/* Status indicator (add button or checkmark) */}
      <Box
        sx={{
          position: "absolute",
          top: 10.4,
          right: 10.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {isSelected ? (
          // Selected: show green checkmark
          <Box
            sx={{
              width: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#15a856",
              borderRadius: "50%",
              transition: "background-color 0.15s ease-in-out",
              "&:hover": {
                bgcolor: "#0d8a42",
              },
            }}
          >
            <Iconify icon="eva:checkmark-fill" width={14} sx={{ color: "#fff" }} />
          </Box>
        ) : (
          // Default: show add circle
          <Box
            sx={{
              color: "text.secondary",
              display: "flex",
              "&:hover": {
                color: "primary.main",
              },
            }}
          >
            <Iconify icon="solar:add-circle-line-duotone" width={20} />
          </Box>
        )}
      </Box>
    </Paper>
  );
}

// ----------------------------------------------------------------------

function TrackedBrandCard({
  brand,
  onDelete,
}: {
  brand: TrackedBrand;
  onDelete?: (brand: TrackedBrand) => void;
}) {
  const { t } = useTranslation();

  return (
    <Paper
      variant="outlined"
      sx={{
        position: "relative",
        borderRadius: 1,
        flexShrink: 0,
        borderColor: "rgba(145, 158, 171, 0.2)",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Main content */}
      <Box sx={{ p: 1.5, pb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
        {/* Header with logo and name */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            component="img"
            src={`https://www.google.com/s2/favicons?domain=${brand.domains[0]}&sz=48`}
            alt={brand.name}
            sx={{
              width: 24,
              height: 24,
              borderRadius: 0.5,
              objectFit: "cover",
            }}
          />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary" }}>
            {brand.name}
          </Typography>
        </Stack>

        {/* Domains list */}
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {brand.domains.join(", ")}
        </Typography>
      </Box>

      {/* Delete button */}
      <IconButton
        size="small"
        onClick={() => onDelete?.(brand)}
        sx={{
          position: "absolute",
          top: 11,
          right: 11,
          p: 0,
          color: "#b1b9c4",
          "&:hover": {
            color: "error.main",
          },
        }}
      >
        <Iconify icon="solar:trash-bin-trash-bold" width={14} />
      </IconButton>
    </Paper>
  );
}

// ----------------------------------------------------------------------

function SuggestedBrandCardSkeleton() {
  return (
    <Paper
      variant="outlined"
      sx={{
        position: "relative",
        borderRadius: 1,
        overflow: "hidden",
        flexShrink: 0,
        borderColor: "rgba(145, 158, 171, 0.2)",
        bgcolor: "background.paper",
      }}
    >
      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="rounded" width={24} height={24} />
          <Skeleton variant="text" width={120} height={20} />
        </Stack>
        <Skeleton variant="text" width="80%" height={16} />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: "rgba(145, 158, 171, 0.08)",
        }}
      >
        <Skeleton variant="text" width={80} height={16} />
        <Skeleton variant="text" width={50} height={16} />
      </Box>
    </Paper>
  );
}

// ----------------------------------------------------------------------

function TrackedBrandCardSkeleton() {
  return (
    <Paper
      variant="outlined"
      sx={{
        position: "relative",
        borderRadius: 1,
        flexShrink: 0,
        borderColor: "rgba(145, 158, 171, 0.2)",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="rounded" width={24} height={24} />
          <Skeleton variant="text" width={120} height={20} />
        </Stack>
        <Skeleton variant="text" width="80%" height={16} />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: "rgba(145, 158, 171, 0.08)",
        }}
      >
        <Skeleton variant="text" width={80} height={16} />
        <Skeleton variant="text" width={50} height={16} />
      </Box>
    </Paper>
  );
}

// ----------------------------------------------------------------------

function CountryFilterSkeleton() {
  return (
    <Paper
      sx={{
        p: 0,
        overflow: "hidden",
        backgroundColor: "transparent",
        borderBottom: "1px solid rgba(145, 158, 171, 0.20)",
        px: "24px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          py: 1,
        }}
      >
        {Array.from({ length: 5 }).map((_, index) => (
          <Box
            key={`country-skeleton-${index}`}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 2,
              minHeight: 36,
            }}
          >
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="text" width={60 + Math.random() * 40} height={20} />
          </Box>
        ))}
      </Box>
    </Paper>
  );
}

// ----------------------------------------------------------------------

function EmptyState({
  icon,
  title,
  description
}: {
  icon: string;
  title: string;
  description: string;
}) {

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        px: 3,
        textAlign: "center",
        color: "text.secondary",
      }}
    >
      <Iconify
        icon={icon}
        width={48}
        sx={{
          color: "rgba(145, 158, 171, 0.4)",
          mb: 2,
        }}
      />
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          color: "text.primary",
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          maxWidth: 320,
        }}
      >
        {description}
      </Typography>
    </Box>
  );
}

// ----------------------------------------------------------------------

// Props for the useBrandSelection hook
type UseBrandSelectionProps = {
  workspaceId?: number;
  selectedCountry?: string;
};

// Custom hook to manage brand selection state
export function useBrandSelection({ workspaceId, selectedCountry }: UseBrandSelectionProps) {
  // Fetch data from API - skip if no workspace or no country selected
  const {
    data: apiResponse,
    isLoading: isLoadingApi,
    isFetching: isFetchingApi,
  } = useGetBrandSelectionQuery(
    { workspaceId: workspaceId!, selectedCountry },
    { skip: !workspaceId || !selectedCountry }
  );

  // API mutations
  const [trackBrand] = useTrackBrandMutation();
  const [untrackBrand] = useUntrackBrandMutation();

  // Local state for UI interactions - use brand name as identifier
  const [selectedBrandNames, setSelectedBrandNames] = useState<Set<string>>(new Set());
  const [isActivating, setIsActivating] = useState(false);

  // Get data from API response
  const suggestedBrands = useMemo(() => {
    if (apiResponse?.ok && apiResponse.data?.suggested_brands) {
      return apiResponse.data.suggested_brands;
    }
    if (!workspaceId) return [];
    return [];
  }, [apiResponse, workspaceId]);

  const trackedBrands = useMemo(() => {
    if (apiResponse?.ok && apiResponse.data?.tracked_brands) {
      return apiResponse.data.tracked_brands;
    }
    if (!workspaceId) return [];
    return [];
  }, [apiResponse, workspaceId]);

  const selectedCount = selectedBrandNames.size;
  const hasSelection = selectedCount > 0;
  const isRefreshingSuggested = isFetchingApi;

  // Toggle brand selection (instant, no loading)
  const handleToggleBrand = useCallback((brand: SuggestedBrand) => {
    setSelectedBrandNames((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(brand.name)) {
        newSet.delete(brand.name);
      } else {
        newSet.add(brand.name);
      }
      return newSet;
    });
  }, []);

  // Activate scrapping for selected brands
  const handleActivateScrapping = useCallback(async () => {
    if (selectedBrandNames.size === 0 || isActivating || !workspaceId || !selectedCountry) return;

    const selectedBrands = suggestedBrands.filter((b) => selectedBrandNames.has(b.name));

    setIsActivating(true);

    try {
      // Track all selected brands via API using their name, country and domain
      const trackPromises = selectedBrands.map((brand) =>
        trackBrand({
          workspaceId,
          brandName: brand.name,
          country: selectedCountry,
          brandDomain: brand.domains?.[0] || undefined,
        }).unwrap()
      );

      await Promise.all(trackPromises);

      // Clear selection after successful tracking
      setSelectedBrandNames(new Set());
    } catch (error) {
      console.error("Error activating scrapping:", error);
    } finally {
      setIsActivating(false);
    }
  }, [selectedBrandNames, suggestedBrands, isActivating, workspaceId, selectedCountry, trackBrand]);

  // Delete a tracked brand using its database ID
  const handleDeleteBrand = useCallback(async (brand: TrackedBrand) => {
    try {
      await untrackBrand(brand.dbId).unwrap();
    } catch (error) {
      console.error("Error deleting brand:", error);
    }
  }, [untrackBrand]);

  // Add a new brand manually (from modal)
  const handleAddNewBrand = useCallback(async (brandData: { name: string; domains: string; countries: string }): Promise<boolean> => {
    if (!workspaceId || !brandData.name.trim()) return false;

    // Use country from modal if provided, otherwise fall back to page's selected country
    const countryToUse = brandData.countries || selectedCountry;
    if (!countryToUse) {
      console.error("No country selected for brand tracking");
      return false;
    }

    try {
      await trackBrand({
        workspaceId,
        brandName: brandData.name.trim(),
        country: countryToUse,
        brandDomain: brandData.domains.trim() || undefined,
      }).unwrap();
      return true;
    } catch (error) {
      console.error("Error adding brand:", error);
      return false;
    }
  }, [workspaceId, selectedCountry, trackBrand]);

  return {
    suggestedBrands,
    trackedBrands,
    selectedBrandNames,
    selectedCount,
    hasSelection,
    isActivating,
    isRefreshingSuggested,
    isLoading: isLoadingApi,
    handleToggleBrand,
    handleActivateScrapping,
    handleDeleteBrand,
    handleAddNewBrand,
  };
}

// Card dimensions for calculating rows
const SUGGESTED_CARD_HEIGHT = 76;
const SUGGESTED_GAP = 18; // gap: 2.25 = 18px

const TRACKED_CARD_HEIGHT = 76;
const TRACKED_GAP = 16; // gap: 2 = 16px

const VISIBLE_ROWS = 2;

export default function SuggestedBrands() {
  const [showAllSuggested, setShowAllSuggested] = useState(false);
  const [showAllTracked, setShowAllTracked] = useState(false);
  const workspace: any = useCurrentWorkspace()
  const [isAddBrandModalOpen, setIsAddBrandModalOpen] = useState(false);
  const filterStore = useSelector((state: any) => state.filters);

  const { t } = useLocales();

  // Fetch countries data first
  const { data: countriesData, isLoading: isLoadingCountries, isFetching: isFetchingCountries }: any = useGetCountriesQuery(
    {
      workspace_id: workspace?.bqTable,
    }
  );

  const brandSelection = useBrandSelection({
    workspaceId: workspace?.id,
    selectedCountry: filterStore?.countries[0] || undefined,
  });

  const suggestedBrands = brandSelection.suggestedBrands
  const trackedBrands = brandSelection.trackedBrands
  const selectedBrandNames = brandSelection.selectedBrandNames
  const isRefreshingSuggested = brandSelection.isRefreshingSuggested
  const isBrandsLoading = brandSelection.isLoading
  const onToggleBrand = brandSelection.handleToggleBrand
  const onDeleteBrand = brandSelection.handleDeleteBrand

  // Combined loading state: show skeletons when countries or brands are loading
  const isLoading = isLoadingCountries || isBrandsLoading

  // Calculate max height for 2 rows (+ padding to avoid truncation)
  const suggestedMaxHeight = VISIBLE_ROWS * SUGGESTED_CARD_HEIGHT + (VISIBLE_ROWS - 1) * SUGGESTED_GAP + 20;
  const trackedMaxHeight = VISIBLE_ROWS * TRACKED_CARD_HEIGHT + (VISIBLE_ROWS - 1) * TRACKED_GAP + 20;

  // Estimate if we need "show more" button (rough calculation assuming ~4 cards per row)
  const suggestedNeedsExpand = suggestedBrands.length > 8;
  const trackedNeedsExpand = trackedBrands.length > 10;

  return (
    <>
      <Helmet>
        <title>Brand Pulse</title>
      </Helmet>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 9 }}>


        {isLoadingCountries ? (
          <CountryFilterSkeleton />
        ) : (
          countriesData && countriesData.length > 1 && (
            <RegionSelector data={countriesData} />
          )
        )}

        <Box px={'24px'} pt={countriesData && countriesData?.length > 1 ? 0 : 4}>

          {/* Suggested Brands Section */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Stack direction="row" justifyContent={'space-between'} spacing={1} alignItems="center">
              <Box display={"flex"} flexDirection={'row'} gap={1}>
                <CustomTooltip
                  title={t('dashboard.global.brandPulse.definitions.suggested_brands')}
                  placement="top"
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "text.primary", cursor: 'help' }}>
                    {t('dashboard.global.brandPulse.suggested_brands')}
                  </Typography>
                </CustomTooltip>
                {isRefreshingSuggested && (
                  <CircularProgress size={16} sx={{ color: "text.secondary", position: 'relative', top: 5 }} />
                )}
              </Box>


              {/* Right side: Action buttons (only on Brand selection page) */}
              <Stack direction="row" spacing={1.5} alignItems="center">
                {/* Add new brand button */}
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="eva:plus-fill" width={18} />}
                  onClick={() => setIsAddBrandModalOpen(true)}
                  sx={{
                    borderColor: "rgba(145, 158, 171, 0.32)",
                    color: "text.primary",
                    fontWeight: 600,
                    fontSize: 13,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "rgba(145, 158, 171, 0.5)",
                      bgcolor: "rgba(145, 158, 171, 0.08)",
                    },
                  }}
                >
                  {t('dashboard.global.brandPulse.add_new_brand')}
                </Button>

                {/* Activate scrapping button */}
                <Button
                  variant="contained"
                  disabled={!brandSelection.hasSelection}
                  onClick={brandSelection.handleActivateScrapping}
                  startIcon={
                    brandSelection.isActivating ? (
                      <CircularProgress size={14} sx={{ color: "inherit" }} />
                    ) : null
                  }
                  sx={{
                    bgcolor: "#181818",
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 13,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    textTransform: "none",
                    opacity: brandSelection.hasSelection ? 1 : 0.2,
                    "&:hover": {
                      bgcolor: "#333",
                    },
                    "&.Mui-disabled": {
                      bgcolor: "#181818",
                      color: "#fff",
                    },
                  }}
                >
                  {brandSelection.isActivating
                    ? "Activating..."
                    : `${t('dashboard.global.brandPulse.activate')} ${brandSelection.selectedCount > 0 ? ` (${brandSelection.selectedCount.toString().padStart(2, "0")})` : ""}`
                  }
                </Button>
              </Stack>
            </Stack>



            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {/* Cards grid with collapsible container */}
              {isLoading ? (
                // Show skeletons during loading
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(5, 1fr)",
                    gap: 1.25,
                  }}
                >
                  {Array.from({ length: 6 }).map((_, index) => (
                    <SuggestedBrandCardSkeleton key={`skeleton-suggested-${index}`} />
                  ))}
                </Box>
              ) : suggestedBrands.length === 0 ? (
                // Empty state
                <EmptyState
                  icon="solar:magnifer-line-duotone"
                  title={t('dashboard.global.brandPulse.no_suggested_brands')}
                  description={t('dashboard.global.brandPulse.descriptions.no_suggested_brands')}
                />
              ) : (
                // Show actual cards
                <>
                  <Box
                    sx={{
                      maxHeight: showAllSuggested ? "none" : `${suggestedMaxHeight}px`,
                      overflow: "hidden",
                      transition: "max-height 0.3s ease-in-out",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, 1fr)", // 5 colonnes égales
                        gap: 1.5, // 18px, même que ton gap précédent
                      }}
                    >
                      {suggestedBrands.map((brand) => (
                        <SuggestedBrandCard
                          key={brand.name}
                          brand={brand}
                          isSelected={selectedBrandNames.has(brand.name)}
                          onToggle={onToggleBrand}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Show more/less button */}
                  {suggestedNeedsExpand && (
                    <Button
                      variant="text"
                      onClick={() => setShowAllSuggested(!showAllSuggested)}
                      endIcon={
                        <Iconify
                          icon={showAllSuggested ? "eva:arrow-ios-upward-fill" : "eva:arrow-ios-downward-fill"}
                          width={18}
                        />
                      }
                      sx={{
                        alignSelf: "flex-start",
                        color: "#0052b4",
                        fontWeight: 500,
                        fontSize: 13,
                        px: 0.5,
                        "&:hover": {
                          bgcolor: "transparent",
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {showAllSuggested ? "Show less" : "Show more"}
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Box>

          {/* Tracked Brands Section */}
          <Box>
            <CustomTooltip
              title={t('dashboard.global.brandPulse.definitions.tracked_brands')}
              placement="top"
              PopperProps={{
                modifiers: [
                  {
                    name: "offset",
                    options: {
                      offset: [0, -30],
                    },
                  },
                ],
              }}
            >
              <Box display="inline-block">
                <Typography
                  mt={4}
                  variant="h6"
                  sx={{ fontWeight: 700, color: "text.primary", cursor: "help" }}
                >
                  {t('dashboard.global.brandPulse.tracked_brands')}
                </Typography>
              </Box>
            </CustomTooltip>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mt: 3 }}>
              {isLoading ? (
                // Show skeletons during loading
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(5, 1fr)",
                    gap: 1.5,
                  }}
                >
                  {Array.from({ length: 4 }).map((_, index) => (
                    <TrackedBrandCardSkeleton key={`skeleton-tracked-${index}`} />
                  ))}
                </Box>
              ) : trackedBrands.length === 0 ? (
                // Empty state
                <EmptyState
                  icon="solar:bookmark-line-duotone"
                  title={t('dashboard.global.brandPulse.no_tracked_brands')}
                  description={t('dashboard.global.brandPulse.descriptions.no_tracked_brands')}
                />
              ) : (
                // Show actual cards
                <>
                  <Box
                    sx={{
                      maxHeight: showAllTracked ? "none" : `${trackedMaxHeight}px`,
                      overflow: "hidden",
                      transition: "max-height 0.3s ease-in-out",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "repeat(5, 1fr)",
                        gap: 1.25,
                      }}
                    >
                      {trackedBrands.map((brand) => (
                        <TrackedBrandCard key={brand.name} brand={brand} onDelete={onDeleteBrand} />
                      ))}
                    </Box>
                  </Box>

                  {/* Show more/less button */}
                  {trackedNeedsExpand && (
                    <Button
                      variant="text"
                      onClick={() => setShowAllTracked(!showAllTracked)}
                      endIcon={
                        <Iconify
                          icon={showAllTracked ? "eva:arrow-ios-upward-fill" : "eva:arrow-ios-downward-fill"}
                          width={18}
                        />
                      }
                      sx={{
                        alignSelf: "flex-start",
                        color: "#0052b4",
                        fontWeight: 500,
                        fontSize: 13,
                        px: 0.5,
                        "&:hover": {
                          bgcolor: "transparent",
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {showAllTracked ? "Show less" : "Show more"}
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
      {/* Add Brand Modal */}
      <AddBrandModal
        open={isAddBrandModalOpen}
        onClose={() => setIsAddBrandModalOpen(false)}
        onAddBrand={brandSelection.handleAddNewBrand}
      />
    </>
  );
}
